import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CircularProgress, Divider, TextField, Typography } from '@mui/material';

import { adminApi } from './api/clients';
import { fetchAdminSession } from './api/staffAccess';
import {
    clearAdminSession,
    saveAdminSession,
    isMicrosoftSignInConfigured,
    isPasswordSignInOffered,
} from './utils/adminSession';

/**
 * Staff sign-in for the approval screens.
 *
 * Microsoft is the way in: it is the only one that proves who is acting, and an approval records
 * a name. The employee number and shared password below it exist for dev and uat until the
 * Microsoft app registration is in place — the backend refuses them in production whatever this
 * page offers.
 */
const AdminLogin = () => {
    const navigate = useNavigate();
    const [employeeId, setEmployeeId] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [signingIn, setSigningIn] = useState(false);

    const microsoftReady = isMicrosoftSignInConfigured();
    const passwordOffered = isPasswordSignInOffered();

    /**
     * Whether the server's answer means this person can actually do anything here.
     *
     * Signing in with Microsoft proves who somebody is. It does not say they have been given
     * access to these screens — an AIC account is an AIC account, and most of them belong to
     * people with no business approving re-enrolments.
     *
     * Somebody who hands out access counts even with no campus of their own: that is the screen
     * they are here for.
     */
    const hasStaffAccess = (session = {}) => (
        session.allCampuses === true
        || (Array.isArray(session.campuses) && session.campuses.length > 0)
        || session.canManageAccess === true
    );

    const NO_ACCESS = 'Your AIC account has not been given access to the staff screens. '
        + 'Ask whoever looks after access to add you.';

    const signInWithPassword = async (event) => {
        event.preventDefault();
        setError('');
        setSigningIn(true);

        try {
            const { data } = await adminApi.post('/admin-login', {
                employeeId: employeeId.trim(),
                password,
            });

            // The same rule as Microsoft: an accepted password is not the same as having been
            // given a campus to look at.
            if (!hasStaffAccess(data)) {
                setError(NO_ACCESS);
                return;
            }

            saveAdminSession({
                token: data.token,
                name: data.employeeId,
                signInMethod: data.signInMethod,
                campuses: data.campuses,
                allCampuses: data.allCampuses,
                accessLevel: data.accessLevel,
                canManageAccess: data.canManageAccess,
                team: data.team,
            });
            navigate('/approver-screen');
        } catch (requestError) {
            setError(
                requestError?.response?.data?.error
                || 'Could not sign in. Please check your employee number and password.'
            );
        } finally {
            setSigningIn(false);
        }
    };

    const signInWithMicrosoft = async () => {
        setError('');
        setSigningIn(true);

        try {
            // Loaded here rather than at the top so a build without Microsoft configured does not
            // carry it at all.
            const { PublicClientApplication } = await import('@azure/msal-browser');

            const msal = new PublicClientApplication({
                auth: {
                    clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
                    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID}`,
                    redirectUri: window.location.origin + '/admin-login',
                },
                cache: { cacheLocation: 'sessionStorage' },
            });

            await msal.initialize();
            const result = await msal.loginPopup({ scopes: ['openid', 'profile', 'email'] });

            const name = result.account?.username || result.account?.name || '';
            saveAdminSession({ token: result.idToken, name, signInMethod: 'microsoft' });

            // A Microsoft sign-in happens in the browser and never passes through us, so this
            // is the first and only point where the server gets to refuse. It used to be allowed
            // to fail quietly, which let anybody with an AIC account onto the dashboard to be
            // told no campus had been assigned to them — as though they were in and waiting on a
            // setting, rather than never admitted.
            let session;
            try {
                session = await fetchAdminSession();
            } catch (sessionError) {
                clearAdminSession();
                const status = sessionError?.response?.status;
                setError(status === 401 || status === 403
                    ? NO_ACCESS
                    : 'Could not check your access just now. Please try again.');
                return;
            }

            if (!hasStaffAccess(session)) {
                // Known to the server, holding nothing: signed in, and unable to see one family.
                clearAdminSession();
                setError(NO_ACCESS);
                return;
            }

            saveAdminSession({
                token: result.idToken,
                name,
                signInMethod: 'microsoft',
                campuses: session.campuses,
                allCampuses: session.allCampuses,
                accessLevel: session.accessLevel,
                canManageAccess: session.canManageAccess,
                team: session.team,
            });

            navigate('/approver-screen');
        } catch (signInError) {
            setError(signInError?.message || 'Microsoft sign-in did not complete.');
        } finally {
            setSigningIn(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', p: 2 }}>
            <Card sx={{ width: '100%', maxWidth: 420, p: 4, borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>
                    Staff sign in
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#64748b', mt: 0.5, mb: 3 }}>
                    For reviewing and approving the changes parents have made.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {microsoftReady && (
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={signingIn}
                        onClick={signInWithMicrosoft}
                        sx={{ bgcolor: '#0f172a', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#1e293b' } }}
                    >
                        {signingIn ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Sign in with Microsoft'}
                    </Button>
                )}

                {microsoftReady && passwordOffered && (
                    <Divider sx={{ my: 3, color: '#94a3b8', fontSize: '12px' }}>or</Divider>
                )}

                {passwordOffered && (
                    <Box component="form" onSubmit={signInWithPassword}>
                        {!microsoftReady && (
                            <Alert severity="info" sx={{ mb: 2, fontSize: '13px' }}>
                                Microsoft sign-in is not set up for this environment yet, so this is
                                a temporary sign-in for testing.
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="Employee number"
                            value={employeeId}
                            onChange={(event) => setEmployeeId(event.target.value)}
                            sx={{ mb: 2 }}
                            inputProps={{ 'aria-label': 'Employee number' }}
                        />
                        <TextField
                            fullWidth
                            type="password"
                            label="Password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            sx={{ mb: 3 }}
                            inputProps={{ 'aria-label': 'Password' }}
                        />
                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={signingIn || !employeeId.trim() || !password}
                            sx={{ bgcolor: '#059669', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#047857' } }}
                        >
                            {signingIn ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Sign in'}
                        </Button>
                    </Box>
                )}

                {!microsoftReady && !passwordOffered && (
                    <Alert severity="warning">
                        Staff sign-in has not been set up for this environment.
                    </Alert>
                )}
            </Card>
        </Box>
    );
};

export default AdminLogin;
